import { useContext, useState } from 'react';
import * as R from 'ramda';

import WorkTemplateTree from '@atom/components/common/workTemplateTree/WorkTemplateTree';
import { Button, Checkbox, List, Modal } from '@atom/mui';
import { WorkTemplate } from '@atom/types/workTemplate';

import WorkOrdersFiltersContext from './WorkOrdersFiltersContext';

import classes from './filters.module.css';

const { ListItem, ListItemText } = List;

const styles = {
  button: {
    minWidth: 0,
    padding: 0,
  },
  modal: {
    height: '50vh',
    padding: '2rem',
  },
  checkbox: {
    padding: 0,
    marginRight: '1rem',
  },
};

const TemplateFilter = () => {
  const { schemaId, areFiltersDisabled, filterForm, updateFilterForm } =
    useContext(WorkOrdersFiltersContext);

  const {
    hasWorkTemplate: hasWorkTemplateFilter,
    workTemplates: workTemplateFilter,
  } = filterForm;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [hasWorkTemplate, setHasWorkTemplate] = useState<boolean | null>(
    filterForm.hasWorkTemplate,
  );
  const [selectedTemplates, setSelectedTemplates] = useState<WorkTemplate[]>(
    [],
  );

  const selectedTemplateIds: Set<string> = new Set(
    selectedTemplates.map(template => template.id),
  );

  const handleHasWorkTemplate = (event, isAll: boolean) => {
    setSelectedTemplates([]);
    setHasWorkTemplate(event.target.checked ? isAll : null);
  };

  const handleToggleWorkTemplate = toggleTemplate => {
    const isSelected = selectedTemplateIds.has(toggleTemplate.id);
    const updatedSelected = isSelected
      ? selectedTemplates.filter(template => template.id !== toggleTemplate.id)
      : [...selectedTemplates, toggleTemplate];
    setSelectedTemplates(updatedSelected);
  };

  const onOpenModal = () => {
    setIsModalOpen(true);
    setHasWorkTemplate(hasWorkTemplateFilter);
    setSelectedTemplates(workTemplateFilter);
  };

  const onCancelModal = () => {
    setHasWorkTemplate(hasWorkTemplateFilter);
    setSelectedTemplates(workTemplateFilter);
    setIsModalOpen(false);
  };

  const onConfirmModal = () => {
    updateFilterForm('hasWorkTemplate', hasWorkTemplate);
    updateFilterForm('workTemplates', selectedTemplates);
    setIsModalOpen(false);
  };

  const onClearModal = () => {
    setHasWorkTemplate(null);
    setSelectedTemplates([]);
  };

  const onClearFilter = () => {
    updateFilterForm('hasWorkTemplate', null);
    updateFilterForm('workTemplates', []);
  };

  const showHasWorkTemplateText: boolean = !R.isNil(hasWorkTemplateFilter);
  const showClearButton: boolean =
    !R.isNil(hasWorkTemplateFilter) || !R.isEmpty(workTemplateFilter);

  return (
    <>
      <div className={classes.filterContainer}>
        <div className={classes.filterLabel}>Work Template</div>
        <div>
          {showHasWorkTemplateText &&
            (hasWorkTemplateFilter
              ? 'All Work Templates'
              : 'Without Work Template')}
          {workTemplateFilter.map(template => template.name).join(', ')}
        </div>
        <div className={classes.filterButtonPair}>
          <Button
            size="small"
            onClick={onOpenModal}
            style={styles.button}
            disabled={areFiltersDisabled}
            data-cy="workOrdersFilterTemplateSelectButton"
          >
            select
          </Button>
          {showClearButton && (
            <Button
              size="small"
              onClick={onClearFilter}
              style={styles.button}
              disabled={areFiltersDisabled}
              data-cy="workOrdersFilterTemplateClearButton"
            >
              clear
            </Button>
          )}
        </div>
      </div>
      <Modal
        open={isModalOpen}
        title="Select Work Template"
        onCancel={onCancelModal}
        onConfirm={onConfirmModal}
        onClear={onClearModal}
        width="lg"
        contentStyle={styles.modal}
        data-cy="workOrdersFilterTemplateModal"
        CancelButtonProps={{
          'data-cy': 'workOrdersFilterTemplateModalCancelButton',
        }}
        ConfirmButtonProps={{
          'data-cy': 'workOrdersFilterTemplateModalConfirmButton',
        }}
        ClearButtonProps={{
          'data-cy': 'workOrdersFilterTemplateModalClearButton',
        }}
      >
        <WorkTemplateTree
          includeSearch
          schemaId={schemaId}
          hasSchema={false}
          getTemplateDisabled={() => !R.isNil(hasWorkTemplate)}
          getTemplateSelected={template =>
            R.equals(hasWorkTemplate, true) ||
            selectedTemplateIds.has(template.id)
          }
          onTemplateToggle={handleToggleWorkTemplate}
          additionalContentTop={
            <>
              <ListItem disableGutters>
                <Checkbox
                  style={styles.checkbox}
                  checked={R.equals(hasWorkTemplate, false)}
                  onChange={event => handleHasWorkTemplate(event, false)}
                  data-cy="workOrdersFilterWithoutTemplateCheckbox"
                />
                <ListItemText>Without Work Template</ListItemText>
              </ListItem>
              <ListItem disableGutters>
                <Checkbox
                  style={styles.checkbox}
                  checked={R.equals(hasWorkTemplate, true)}
                  onChange={event => handleHasWorkTemplate(event, true)}
                  data-cy="workOrdersFilterAllTemplatesCheckbox"
                />
                <ListItemText>All Work Templates</ListItemText>
              </ListItem>
            </>
          }
        />
      </Modal>
    </>
  );
};

export default TemplateFilter;
